import {
  Brand,
  Family,
  FamilySegment,
  FamilySegments,
  FamilyVersion,
  Model,
  Segment,
  SegmentModel,
  tabs, Tag,
  VehicleType, VehicleTypeOriginRange,
} from '@/modules/neovehicle/models';
import {
  ClassificationFamilyFilter,
  ClassificationModelFilter, DncClientContractFilter,
  DncFamilyFilter, DncFamilyGroupFailsFilter,
  DncFamilyGroupFilter,
  FamilyFilter,
  FamilyGroupFilter,
  FamilyVersionFilter,
} from '@/modules/neovehicle/models/filters';
import {modelService} from '@/modules/neovehicle/services/model.service';
import {familyService} from '@/modules/neovehicle/services/family.service';
import {
  GetterTypes,
  ActionTypes,
  MutationTypes,
} from '@/modules/neovehicle/store/types';
import {CountryEnum} from '@/models/Enums';
import {brandService} from '@/modules/neovehicle/services/brand.service';
import {segmentService} from '@/modules/neovehicle/services/segment.service';
import {
  FamiliesToFamilyGroupEdit,
  FamilyEdit, FamilyGroupEdit,
  FamilyGroupManagement,
  FamilyManagement,
  FamilySegmentsReport,
  FamilyVersionEdit,
  FamilyVersionFamiliesEdit,
  FamilyVersionManagement,
  FamilyVersionModelsEdit,
  LastClassifications,
  FamilyGroupCreateModel,
  FamilyCreateModel,
} from '@/modules/neovehicle/models/family';
import { Market } from '@/modules/neovehicle/models/market';
import {
  DncClientContract,
  DncFamily,
  DncFamilyGroup,
  DncFamilyGroupFails,
  DncFamilyGroupFamily,
} from '@/modules/neovehicle/models/dnc';
import {dncFamilyService} from '@/modules/neovehicle/services/dnc.service';
import { SocialClass } from '../models/socialClass';

interface State {
  models: Model[];
  modelClassificationSelected: Model[] | null;
  modelEditSelected: Model[] | null;
  currentTab: string;
  brands: Brand[];
  familyVersions: FamilyVersion[];
  familySegmentSelected: FamilySegment | null;
  familiesSegment: FamilySegment[];
  segmentsByModel: SegmentModel[];
  brandsWithContracts: Brand[];
  segmentsByBrand: Segment[];
  familiesBySegment: Family[];
  familySegments: FamilySegments[];
  tagsByContext: Tag[];
  vehicleTypeOriginsRange: VehicleTypeOriginRange[];
  familiesWithSegment: Family[];
  familyVersionsManagement: FamilyVersionManagement[];
  familiesManagement: FamilyManagement[];
  familyGroupsManagement: FamilyGroupManagement[];
  lastClassifications: LastClassifications | null;
  dncFamilies: DncFamily[];
  dncFamilyGroups: DncFamilyGroup[];
  dncClientContracts: DncClientContract[];
  dncFamilyGroupFails: DncFamilyGroupFails[];
  socialClasses: SocialClass[];
}

const initialState = {
  models: [],
  modelClassificationSelected: null,
  modelEditSelected: null,
  currentTab: tabs.list,
  brands: [],
  familyVersions: [],
  familySegmentSelected: null,
  familiesSegment: [],
  segmentsByModel: [],
  brandsWithContracts: [],
  segmentsByBrand: [],
  familiesBySegment: [],
  familySegments: [],
  tagsByContext: [],
  vehicleTypeOriginsRange: [],
  familiesWithSegment: [],
  familyVersionsManagement: [],
  familiesManagement: [],
  familyGroupsManagement: [],
  lastClassifications: null,
  dncFamilies: [],
  dncFamilyGroups: [],
  dncClientContracts: [],
  dncFamilyGroupFails: [],
  socialClasses: [],
};

const storeState = (): State => ({...initialState});

const getters = {
  [GetterTypes.GET_CURRENT_TAB](state: State): string {
    return state.currentTab;
  },

  [GetterTypes.GET_MODELS](state: State): Model[] {
    return state.models;
  },

  [GetterTypes.GET_MODEL_CLASSIFICATION_SELECTED](state: State): Model[] | null {
    return state.modelClassificationSelected;
  },

  [GetterTypes.GET_MODEL_EDIT_SELECTED](state: State): Model[] | null {
    return state.modelEditSelected;
  },

  [GetterTypes.GET_BRANDS](state: State): Brand[] {
    return state.brands;
  },

  [GetterTypes.GET_FAMILY_VERSIONS](state: State): FamilyVersion[] {
    return state.familyVersions;
  },

  [GetterTypes.GET_FAMILY_SEGMENT_SELECTED](
    state: State,
  ): FamilySegment | null {
    return state.familySegmentSelected;
  },

  [GetterTypes.GET_FAMILIES_SEGMENT](state: State): FamilySegment[] {
    return state.familiesSegment;
  },

  [GetterTypes.GET_SEGMENT_BY_MODEL](state: State): SegmentModel[] {
    return state.segmentsByModel;
  },

  [GetterTypes.GET_BRAND_WITH_CONTRACTS](state: State): Brand[] {
    return state.brandsWithContracts;
  },

  [GetterTypes.GET_SEGMENTS_BY_BRAND](state: State): Segment[] {
    return state.segmentsByBrand;
  },

  [GetterTypes.GET_FAMILIES_BY_SEGMENT](state: State): Family[] {
    return state.familiesBySegment;
  },

  [GetterTypes.GET_FAMILY_SEGMENTS](
    state: State,
  ): FamilySegments[] {
    return state.familySegments;
  },

  [GetterTypes.GET_TAGS_BY_FAMILY_CONTEXT](
    state: State,
  ): Tag[] | null {
    return state.tagsByContext;
  },

  [GetterTypes.GET_VEHICLE_TYPE_ORIGINS_RANGE](state: State): VehicleTypeOriginRange[] {
    return state.vehicleTypeOriginsRange;
  },

  [GetterTypes.GET_FAMILIES_WITH_SEGMENTS](state: State): Family[] {
    return state.familiesWithSegment;
  },

  [GetterTypes.GET_FAMILY_VERSIONS_MANAGEMENT](state: State): FamilyVersionManagement[] {
    return state.familyVersionsManagement;
  },

  [GetterTypes.GET_FAMILIES_MANAGEMENT](state: State): FamilyManagement[] {
    return state.familiesManagement;
  },

  [GetterTypes.GET_FAMILY_GROUPS_MANAGEMENT](state: State): FamilyGroupManagement[] {
    return state.familyGroupsManagement;
  },

  [GetterTypes.GET_LAST_CLASSIFICATIONS](state: State): LastClassifications | null {
    return state.lastClassifications;
  },

  [GetterTypes.GET_DNC_FAMILIES](state: State): DncFamily[] {
    return state.dncFamilies;
  },

  [GetterTypes.GET_DNC_FAMILY_GROUPS](state: State): DncFamilyGroup[] {
    return state.dncFamilyGroups;
  },

  [GetterTypes.GET_DNC_CLIENT_CONTRACTS](state: State): DncClientContract[] {
    return state.dncClientContracts;
  },

  [GetterTypes.GET_DNC_FAMILY_GROUP_FAILS](state: State): DncFamilyGroupFails[] {
    return state.dncFamilyGroupFails;
  },

  [GetterTypes.GET_SOCIAL_CLASSES](state: State): SocialClass[] {
    return state.socialClasses;
  },
};

const mutations = {
  [MutationTypes.SET_CURRENT_TAB](state: State, payload: string) {
    state.currentTab = payload;
  },

  [MutationTypes.SET_MODELS](state: State, payload: Model[]) {
    state.models = payload;
  },

  [MutationTypes.SET_MODEL_CLASSIFICATION_SELECTED](
    state: State,
    payload: Model[],
  ) {
    state.modelClassificationSelected = payload;
  },

  [MutationTypes.SET_MODEL_EDIT_SELECTED](state: State, payload: Model[]) {
    state.modelEditSelected = payload;
  },

  [MutationTypes.SET_BRANDS](state: State, payload: Brand[]) {
    state.brands = payload;
  },

  [MutationTypes.SET_FAMILY_VERSIONS](state: State, payload: FamilyVersion[]) {
    state.familyVersions = payload;
  },

  [MutationTypes.CLEAR_STORE](state: State) {
    Object.assign(state, initialState);
  },

  [MutationTypes.SET_FAMILY_SEGMENT_SELECTED](
    state: State,
    payload: FamilySegment,
  ) {
    state.familySegmentSelected = payload;
  },

  [MutationTypes.SET_FAMILIES_SEGMENT](state: State, payload: FamilySegment[]) {
    state.familiesSegment = payload;
  },

  [MutationTypes.SET_SEGMENT_BY_MODEL](state: State, payload: SegmentModel[]) {
    state.segmentsByModel = payload;
  },

  [MutationTypes.SET_BRAND_WITH_CONTRACTS](state: State, payload: Brand[]) {
    state.brandsWithContracts = payload;
  },

  [MutationTypes.SET_SEGMENTS_BY_BRAND](state: State, payload: Segment[]) {
    state.segmentsByBrand = payload;
  },

  [MutationTypes.SET_FAMILIES_BY_SEGMENT](state: State, payload: Family[]) {
    state.familiesBySegment = payload;
  },

  [MutationTypes.SET_FAMILY_SEGMENTS](
    state: State,
    payload: FamilySegments[],
  ) {
    state.familySegments = payload;
  },

  [MutationTypes.SET_TAGS_BY_FAMILY_CONTEXT](
    state: State,
    payload: Tag[],
  ) {
    state.tagsByContext = payload;
  },

  [MutationTypes.SET_VEHICLE_TYPE_ORIGINS_RANGE](state: State, payload: VehicleTypeOriginRange[]) {
    state.vehicleTypeOriginsRange = payload;
  },

  [MutationTypes.SET_FAMILIES_WITH_SEGMENT](state: State, payload: Family[]) {
    state.familiesWithSegment = payload;
  },

  [MutationTypes.SET_FAMILY_VERSIONS_MANAGEMENT](state: State, payload: FamilyVersionManagement[]) {
    state.familyVersionsManagement = payload;
  },

  [MutationTypes.SET_FAMILIES_MANAGEMENT](state: State, payload: FamilyManagement[]) {
    state.familiesManagement = payload;
  },

  [MutationTypes.SET_FAMILY_GROUPS_MANAGEMENT](state: State, payload: FamilyGroupManagement[]) {
    state.familyGroupsManagement = payload;
  },

  [MutationTypes.SET_LAST_CLASSIFICATIONS](state: State, payload: LastClassifications) {
    state.lastClassifications = payload;
  },

  [MutationTypes.SET_DNC_FAMILIES](state: State, payload: DncFamily[]) {
    state.dncFamilies = payload;
  },

  [MutationTypes.SET_DNC_FAMILY_GROUPS](state: State, payload: DncFamilyGroup[]) {
    state.dncFamilyGroups = payload;
  },

  [MutationTypes.SET_DNC_CLIENT_CONTRACTS](state: State, payload: DncClientContract[]) {
    state.dncClientContracts = payload;
  },

  [MutationTypes.SET_DNC_FAMILY_GROUP_FAILS](state: State, payload: DncFamilyGroupFails[]) {
    state.dncFamilyGroupFails = payload;
  },

  [MutationTypes.SET_SOCIAL_CLASSES](state: State, payload: SocialClass[]) {
    state.socialClasses = payload;
  },
};

const actions = {
  [ActionTypes.UPDATE_CURRENT_TAB]({commit}: any, payload: string): void {
    commit(MutationTypes.SET_CURRENT_TAB, payload);
  },

  async [ActionTypes.FETCH_MODELS](
    {commit}: any,
    filter: ClassificationModelFilter,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      modelService
        .getClassificationModels(filter)
        .then((result) => result.data)
        .then((models) => {
          commit(MutationTypes.SET_MODELS, models);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  [ActionTypes.UPDATE_MODEL_CLASSIFICATION_SELECTED](
    {commit}: any,
    payload: Model[],
  ): void {
    commit(MutationTypes.SET_MODEL_CLASSIFICATION_SELECTED, payload);
  },

  [ActionTypes.UPDATE_MODEL_EDIT_SELECTED](
    {commit}: any,
    payload: Model[],
  ): void {
    commit(MutationTypes.SET_MODEL_EDIT_SELECTED, payload);
  },

  async [ActionTypes.FETCH_MODELS_EXPORT](
    {commit}: any,
    payload: CountryEnum,
  ): Promise<any> {
    return modelService.exportModels(payload);
  },

  async [ActionTypes.FETCH_BRANDS]({commit}: any): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      brandService
        .getBrands()
        .then((result) => result.data)
        .then((brands) => {
          commit(MutationTypes.SET_BRANDS, brands);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_FAMILY_VERSIONS](
    {commit}: any,
    payload: { idBrand: number; idVehicleTypes: number[]; country: CountryEnum },
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      modelService
        .getFamilies(payload)
        .then((result) => result.data)
        .then((families) => {
          commit(MutationTypes.SET_FAMILY_VERSIONS, families);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  [ActionTypes.UPDATE_FAMILY_VERSIONS]({commit}: any, payload: FamilyVersion[]): void {
    commit(MutationTypes.SET_FAMILY_VERSIONS, payload);
  },

  async [ActionTypes.SAVE_MODEL_CLASSIFICATION](
    {commit}: any,
    payload: Model[],
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const modelReference = payload[0];

      const family = modelReference.familyversion && modelReference.familyversion.family
        ? modelReference.familyversion.family
        : null;

      const familyGroup = modelReference.familyversion
      && modelReference.familyversion.family
      && modelReference.familyversion.family.familygroup
        ? modelReference.familyversion.family.familygroup
        : null;

      const classification = {
        idModels: payload.map((model: Model) => model.id),
        idBrand: modelReference.brand.id,
        idFamily: family ? family.id : 0,
        idFamilyGroup: familyGroup ? familyGroup.id : 0,
        idFamilyVersion: modelReference.familyversion.id,
        country: modelReference.country,
        idVehicleTypes: modelReference.vehicletypes.map((x: VehicleType) =>
          Number(x.id),
        ),
        idTags: family ? family.tags : null,
        familyName: family ? family.name : null,
        familyGroupName: familyGroup ? familyGroup.name : null,
        familyVersionName: modelReference.familyversion.name,
        marketValue: modelReference.marketvalue,
        marketValueReference: modelReference.marketvaluereference,
        idSocialClass: family ? family.idsocialclass : null,
      };

      modelService
        .saveClassification(classification)
        .then(() => resolve(true))
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.SAVE_MODEL](
    {commit}: any,
    payload: Model[],
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const modelReference = payload[0];

      const model = {
        idModels: payload.map((m: Model) => m.id),
        idBrand: modelReference.brand.id,
        country: modelReference.country,
        idVehicleTypes: modelReference.vehicletypes.map((x: VehicleType) =>
          Number(x.id),
        ),
      };

      modelService
        .saveModel(model)
        .then(() => resolve(true))
        .catch(() => reject(false));
    });
  },

  [ActionTypes.CLEAR_STORE]({commit}: any): void {
    commit(MutationTypes.CLEAR_STORE);
  },

  [ActionTypes.UPDATE_FAMILY_SEGMENT_SELECTED](
    {commit}: any,
    payload: FamilySegment,
  ): void {
    commit(MutationTypes.SET_FAMILY_SEGMENT_SELECTED, payload);
  },

  async [ActionTypes.FETCH_FAMILIES_EXPORT](
    {commit}: any,
    payload: CountryEnum,
  ): Promise<any> {
    return familyService.exportFamiliesSegment(payload);
  },

  async [ActionTypes.FETCH_FAMILIES_SEGMENT](
    {commit}: any,
    filter: ClassificationFamilyFilter,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .getFamiliesSegment(filter)
        .then((result) => result.data)
        .then((familiesSegment) => {
          const result: FamilySegment[] = [];

          familiesSegment.map((familieSegment: FamilySegment) => {
            familieSegment.vehicletypes.length > 1
              ? familieSegment.vehicletypes.map((idVehicleType: number) => {
                result.push({...familieSegment, vehicletypes: [idVehicleType]});
              })
              : result.push(familieSegment);
          });

          commit(MutationTypes.SET_FAMILIES_SEGMENT, result);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_SEGMENT_BY_MODEL](
    {commit}: any,
    payload: { modelName: string; country: CountryEnum },
  ): Promise<boolean> {
    if (payload.modelName === '') {
      commit(MutationTypes.SET_SEGMENT_BY_MODEL, []);
      return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve, reject) => {
      segmentService
        .getSegmentsByModel(payload)
        .then((result) => result.data)
        .then((segments) => {
          commit(MutationTypes.SET_SEGMENT_BY_MODEL, segments);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_BRAND_WITH_CONTRACTS](
    {commit}: any,
    payload: { idVehicleTypes: number[], country: CountryEnum },
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      brandService
        .getBrandWithContract(payload)
        .then((result) => result.data)
        .then((brands) => {
          commit(MutationTypes.SET_BRAND_WITH_CONTRACTS, brands);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_SEGMENTS_BY_BRAND](
    {commit}: any,
    payload: { idBrand: number; idVehicleTypes: number[]; country: CountryEnum },
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      segmentService
        .getSegmentByBrand(payload)
        .then((result) => result.data)
        .then((segments) => {
          commit(MutationTypes.SET_SEGMENTS_BY_BRAND, segments);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_FAMILIES_BY_SEGMENT](
    {commit}: any,
    payload: { idBrand: number; idSegment: number; country: CountryEnum },
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .getFamiliesBySegment(payload)
        .then((result) => result.data)
        .then((families) => {
          commit(MutationTypes.SET_FAMILIES_BY_SEGMENT, families);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.SAVE_FAMILY_CLASSIFICATION](
    {commit}: any,
    payload: {
      idFamily: number;
      country: number;
      segments: Array<{ id: number; idBrand: number }>;
    },
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .saveFamiliesSegment(payload)
        .then(() => resolve(true))
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_FAMILY_SEGMENTS](
    {commit}: any,
    payload: { idFamily: number; idVehicleType: number; country: CountryEnum },
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .getFamilySegments(payload)
        .then((result) => result.data)
        .then((familySegments) => {
          const result: FamilySegments[] = familySegments.map((x: any) => ({
            idSegment: x.idsegment,
            segmentName: x.segmentname,
            idBrand: x.idbrand,
          }));

          commit(MutationTypes.SET_FAMILY_SEGMENTS, result);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_TAGS_BY_FAMILY_CONTEXT](
    {commit}: any,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .getTagsByContext()
        .then((result) => result.data)
        .then((tags) => {
          commit(MutationTypes.SET_TAGS_BY_FAMILY_CONTEXT, tags);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_TAGS_BY_FAMILY_VERSION](
    {commit}: any,
    filter: {
      idFamily: number;
      idFamilyVersion: number;
    },
  ): Promise<Tag[]> {
    return new Promise<Tag[]>((resolve, reject) => {
      familyService
        .getTagsByFamilyVersion(filter)
        .then((result) => result.data)
        .then((tags) => {
          resolve(tags);
        })
        .catch(() => reject(null));
    });
  },

  async [ActionTypes.FETCH_VEHICLE_TYPE_ORIGINS_RANGE](
    {commit}: any,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      modelService
        .getVehicleTypeOriginsRange()
        .then((result) => result.data)
        .then((tags) => {
          commit(MutationTypes.SET_VEHICLE_TYPE_ORIGINS_RANGE, tags);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.SAVE_FAMILY_SEGMENT_FROM_OPTION](
    {commit}: any,
    payload: {
      idFamily: number;
      country: number;
      familySegmentOption: number;
      idVehicleType: number;
      idBrands: number[];
    },
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .saveFamilySegmentFromOption(payload)
        .then(() => resolve(true))
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.CLONE_FAMILY_SEGMENT](
    {commit}: any,
    payload: {
      idFamily: number;
      country: number;
      idFamilyClone: number;
      idVehicleType: number;
    },
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .cloneFamilySegment(payload)
        .then(() => resolve(true))
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_FAMILIES_WITH_SEGMENT](
    {commit}: any,
    filter: {
      familyName: string;
      idVehicleType: number;
      country: CountryEnum;
    },
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .getFamiliesWithSegment(filter)
        .then((result) => result.data)
        .then((families) => {
          commit(MutationTypes.SET_FAMILIES_WITH_SEGMENT, families);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_FAMILY_VERSIONS_MANAGEMENT](
    {commit}: any,
    filter: FamilyVersionFilter,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .getFamilyVersions(filter)
        .then((result) => result.data)
        .then((families) => {
          const result = families.map((x: any) => ({
            idFamilyVersion: x.idfamilyversion,
            idFamily: x.idfamily,
            idBrand: x.idbrand,
            idVehicleTypes: x.idvehicletypes,
            idFamilyGroup: x.idfamilygroup,
            brandName: x.brandname,
            familyVersionName: x.familyversionname,
            familyName: x.familyname,
            familyGroupName: x.familygroupname,
            hasModelAssociated: x.hasmodelassociated,
            marketValue: x.marketvalue,
            marketValueReference: x.marketvaluereference,
          }));
          commit(MutationTypes.SET_FAMILY_VERSIONS_MANAGEMENT, result);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_FAMILIES_MANAGEMENT](
    {commit}: any,
    filter: FamilyFilter,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .getFamilies(filter)
        .then((result) => result.data)
        .then((families) => {
          const result = families.map((x: any) => ({
            idFamilyGroup: x.idfamilygroup,
            idFamily: x.idfamily,
            idBrand: x.idbrand,
            idVehicleTypes: x.idvehicletypes,
            brandName: x.brandname,
            familyName: x.familyname,
            familyGroupName: x.familygroupname,
            hasModelAssociated: x.hasmodelassociated,
            hasFamilyVersionAssociated: x.hasfamilyversionassociated,
            idSocialClass: x.idsocialclass,
          }));

          commit(MutationTypes.SET_FAMILIES_MANAGEMENT, result);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_FAMILY_GROUPS_MANAGEMENT](
    {commit}: any,
    filter: FamilyGroupFilter,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .getFamilyGroups(filter)
        .then((result) => result.data)
        .then((families) => {
          const result = families.map((x: any) => ({
            idFamilyGroup: x.idfamilygroup,
            idBrand: x.idbrand,
            idVehicleTypes: x.idvehicletypes,
            brandName: x.brandname,
            familyGroupName: x.familygroupname,
            hasFamiliesAssociated: x.hasfamiliesassociated,
          }));
          commit(MutationTypes.SET_FAMILY_GROUPS_MANAGEMENT, result);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.DELETE_FAMILY_VERSIONS_MANAGEMENT](
    {commit}: any,
    familyVersion: FamilyVersionManagement,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .deleteFamilyVersion(familyVersion)
        .then(() => {
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.DELETE_FAMILIES_MANAGEMENT](
    {commit}: any,
    family: FamilyManagement,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .deleteFamily(family)
        .then(() => {
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.DELETE_FAMILIES_GROUP_MANAGEMENT](
    {commit}: any,
    familyGroup: FamilyGroupManagement,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .deleteFamilyGroup(familyGroup)
        .then(() => {
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.UPDATE_FAMILY_VERSION_MANAGEMENT](
    {commit}: any,
    familyVersion: FamilyVersionEdit,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .updateFamilyVersion(familyVersion)
        .then(() => {
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.UPDATE_FAMILY_MANAGEMENT](
    {commit}: any,
    family: FamilyEdit,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .updateFamily(family)
        .then(() => {
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.UPDATE_FAMILY_GROUP_MANAGEMENT](
    {commit}: any,
    familyGroup: FamilyGroupEdit,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .updateFamilyGroup(familyGroup)
        .then(() => {
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.UPDATE_FAMILY_VERSION_MODELS](
    {commit}: any,
    edit: FamilyVersionModelsEdit,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .updateFamilyVersionModels(edit)
        .then(() => {
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.UPDATE_FAMILY_FROM_VERSIONS](
    {commit}: any,
    edit: FamilyVersionFamiliesEdit,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .updateFamilyFromVersions(edit)
        .then(() => {
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.UPDATE_FAMILY_GROUP_FROM_FAMILIES](
    {commit}: any,
    edit: FamiliesToFamilyGroupEdit,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .updateFamilyGroupFromFamilies(edit)
        .then(() => {
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_LAST_CLASSIFICATIONS](
    {commit}: any,
    country: CountryEnum,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .getLastClassifications(country)
        .then((result) => result.data)
        .then((data: LastClassifications) => {
          const families = data.familySegmentsReport.map((x: FamilySegmentsReport) => {
            const market = new Market(x.marketValue ? x.marketValue.toString() : '0', x.marketValueReference);

            return ({ ...x, marketValue: market.getMoneyFormatted(), marketValueReference: market.getDateFormatted() } as FamilySegmentsReport);
          });

          data.familySegmentsReport = families;

          commit(MutationTypes.SET_LAST_CLASSIFICATIONS, data);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.INSERT_FAMILY_GROUP](
    {commit}: any,
    familyGroup: FamilyGroupCreateModel,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .insertFamilyGroup(familyGroup)
        .then(() => resolve(true))
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.INSERT_FAMILY](
    {commit}: any,
    family: FamilyCreateModel,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .insertFamily(family)
        .then(() => resolve(true))
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_DNC_FAMILIES](
    {commit}: any,
    filter: DncFamilyFilter,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      dncFamilyService
        .getDncFamily(filter)
        .then((result) => result.data)
        .then((data: DncFamily[]) => {
          commit(MutationTypes.SET_DNC_FAMILIES, data);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_DNC_FAMILY_GROUPS](
    {commit}: any,
    filter: DncFamilyGroupFilter,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      dncFamilyService
        .getDncFamilyGroups(filter)
        .then((result) => result.data)
        .then((data: DncFamilyGroup[]) => {
          commit(MutationTypes.SET_DNC_FAMILY_GROUPS, data);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.SAVE_DNC_FAMILY_GROUP_FAMILY](
    {commit}: any,
    payload: DncFamilyGroupFamily,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      dncFamilyService
        .saveDncFamilyGroupFamily(payload)
        .then(() => resolve(true))
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_DNC_CLIENT_CONTRACTS](
    {commit}: any,
    filter: DncClientContractFilter,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      dncFamilyService
        .getDncClientContracts(filter)
        .then((result) => result.data)
        .then((data: DncClientContract[]) => {
          commit(MutationTypes.SET_DNC_CLIENT_CONTRACTS, data);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.SAVE_DNC_FAMILY_GROUP](
    {commit}: any,
    payload: DncFamilyGroup,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      dncFamilyService
        .saveDncFamilyGroup(payload)
        .then(() => resolve(true))
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_DNC_FAMILY_GROUP_FAILS](
    {commit}: any,
    filter: DncFamilyGroupFailsFilter,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      dncFamilyService
        .getDncFamilyGroupFails(filter)
        .then((result) => result.data)
        .then((data: DncFamilyGroupFails[]) => {
          commit(MutationTypes.SET_DNC_FAMILY_GROUP_FAILS, data);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },

  async [ActionTypes.FETCH_DNC_FAMILY_GROUPS_EXPORT](
    {commit}: any,
    payload: CountryEnum,
  ): Promise<any> {
    return dncFamilyService.exportDncFamilyGroups(payload);
  },

  async [ActionTypes.FETCH_SOCIAL_CLASSES](
    {commit}: any,
    filter: {
      country: CountryEnum,
    }): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      familyService
        .getSocialClasses(filter)
        .then((result) => result.data)
        .then((data: SocialClass[]) => {
          commit(MutationTypes.SET_SOCIAL_CLASSES, data);
          resolve(true);
        })
        .catch(() => reject(false));
    });
  },
};

export default {
  namespace: true,
  state: storeState,
  getters,
  mutations,
  actions,
};
